import { Component } from '@theme/component';

/**
 * DAVIDsTEA About us timeline rail.
 *
 * The rail scrolls on its own — it is an overflow container, so a finger, a trackpad and the
 * keyboard all work with no script. This adds the two arrows and the intro pill, and nothing
 * else.
 *
 * The step is not a fixed width here, unlike the other rails in this theme: the six frames
 * measure 387, 671, 802, 681, 405 and 802. So a move lands on the next step's left edge rather
 * than advancing by a constant, which keeps a step flush against the gutter at every stop
 * instead of drifting further into the rail with each press.
 *
 * Both arrows stay hidden until this runs, and the pair only comes in when the rail overflows
 * the band: `data-overflow`, measured on every scroll and every resize rather than counted off
 * the steps. Each one then dims at the stop it cannot move past rather than leaving, so the
 * pair never shifts under the cursor. At rest only the right arrow is live, which is the
 * opening state; a right arrow on its own would strand a mouse user at the end of the rail.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 * @property {HTMLButtonElement} [cta]
 *
 * @extends {Component<Refs>}
 */
export class DtAboutTimeline extends Component {
  requiredRefs = ['rail', 'prev', 'next'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next, cta } = this.refs;

    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    cta?.addEventListener('click', this.#onNext);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(this.#sync);
    this.#observer.observe(rail);

    this.#sync();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next, cta } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    cta?.removeEventListener('click', this.#onNext);
    rail?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Where each step would sit if it were scrolled flush against the rail's left gutter. Read off
   * the live boxes rather than offsetLeft, which answers against the nearest positioned ancestor
   * and would need the rail to claim one just to be measured.
   *
   * @returns {number[]}
   */
  get #stops() {
    const { rail } = this.refs;
    const railLeft = rail.getBoundingClientRect().left;
    const pad = parseFloat(getComputedStyle(rail).paddingInlineStart) || 0;
    const max = rail.scrollWidth - rail.clientWidth;

    return [...rail.querySelectorAll('.dt-about-timeline__step')].map((step) => {
      const left = rail.scrollLeft + (step.getBoundingClientRect().left - railLeft) - pad;
      return Math.min(Math.max(0, left), max);
    });
  }

  /**
   * A pixel of slack on the comparison: browsers land on fractional scroll offsets, and an exact
   * test would pick the step already flush against the gutter and move nowhere.
   *
   * @param {number} direction
   */
  #move(direction) {
    const { rail } = this.refs;
    const stops = this.#stops;
    const here = rail.scrollLeft;

    const target =
      direction > 0
        ? stops.find((stop) => stop > here + 1)
        : [...stops].reverse().find((stop) => stop < here - 1);

    if (target === undefined) return;
    rail.scrollTo({ left: target, behavior: this.#behavior });
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);
  #onScroll = () => this.#sync();

  #sync = () => {
    const { rail, prev, next } = this.refs;
    const max = rail.scrollWidth - rail.clientWidth;

    this.toggleAttribute('data-overflow', max > 1);
    prev.disabled = rail.scrollLeft <= 1;
    next.disabled = max <= 1 || rail.scrollLeft >= max - 1;
  };
}

if (!customElements.get('dt-about-timeline-component')) {
  customElements.define('dt-about-timeline-component', DtAboutTimeline);
}
